import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

import { Button, ErrorBlock, Input, Spinner } from "../components/ui.tsx";
import {
  EMOJI_PREFIX,
  ICON_GROUPS,
  SpaceIcon,
  emojiOf,
  isEmojiIcon,
  normalizeIconKey,
} from "../components/space-icon.tsx";
import { api } from "../api/client.ts";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  页面管理 · 首页空间
 * ═══════════════════════════════════════════════════════════════
 *
 * 首页那一排「按你家的空间挑」。以前是**写死在组件里的五个 emoji**，
 * 而且同一份词表在六个文件里各写了一遍 —— 其中网页版商品列表写成了中文 key
 * （`space=客厅`，商品上存的是 `living`），那个筛选点了永远是空的。
 * 现在只有这一个来源。
 *
 * 🔴 **`key` 是全站共用的词表，不是显示用的标签。**
 *    商品上打的 `spaces`、案例上的 `space`、商品列表的筛选，比的都是它。
 *    所以已经建好的格子**不给改 key** —— 改了等于把已经打过标签的商品
 *    从那一格里摘出去，而前台不会报错，只会显示"这个空间下没有货"。
 *
 * 🔴 **改完要作废前台那份缓存**（`["site-config"]`）。两边是两个查询 ——
 *    只存不作废的话，他保存成功、点去首页一看还是老样子，
 *    会以为没保存上然后反复来回改。这个坑在品类和店铺货架上踩过两次。
 *
 * ⚠️ 图标有**两种画法**，一格一格挑，也能整排一键切：
 *    **矢量图**（站内自带 36 个，装修建材主题）和 **emoji**（最早那一版的样子）。
 *    两者的取舍见 `components/space-icon.tsx` 开头那张表 ——
 *    emoji 活泼、一眼认得出，但各设备字形不一样、也跟不了配色。
 *    这两句写在切换按钮旁边，别让他切完才发现。
 *
 * ⚠️ 做过一版"填 iconfont 项目地址"的，去掉了 —— 它把首页的完整性挂在了
 *    一个外部服务和一次配置上，地址没填 / 填错 / 对方改了编号，首页就缺一块。
 */

interface SpaceRow {
  key: string;
  label: string;
  hint: string;
  icon: string;
  enabled: boolean;
}

interface SpacesCfg {
  items: SpaceRow[];
}

interface SettingRecord {
  key: string;
  value: SpacesCfg;
  updatedAt: string | null;
}

/**
 * emoji → 矢量图 key 的反查表，「整排换成矢量图」用。
 * ⚠️ 手打的 emoji 查不到就保持原样，不硬塞一个不相干的图标。
 */
const VECTOR_BY_EMOJI = new Map(
  ICON_GROUPS.flatMap((g) => g.icons).map((i) => [i.emoji, i.key]),
);

/** 新加一格时的空白行。⚠️ key 留空，让他自己填 —— 它要出现在地址栏上。 */
const BLANK: SpaceRow = { key: "", label: "", hint: "", icon: "sofa", enabled: true };

export function SpacesTab() {
  const qc = useQueryClient();

  const rec = useQuery({
    queryKey: ["admin", "settings", "spaces"],
    queryFn: () => api.get<SettingRecord>("/admin/settings/spaces"),
  });

  const [draft, setDraft] = useState<SpacesCfg | null>(null);
  /** ⚠️ 只在第一次拿到数据时铺进表单，别每次 refetch 都把他正在打的字盖掉。 */
  useEffect(() => {
    if (!rec.data || draft !== null) return;
    /**
     * ⚠️ 顺手把老值上的 `builtin:` 前缀剥掉（早先图标有两个来源）——
     *    显示时前台本来就兼容，但存回去时清干净，免得两种写法一直混着。
     */
    setDraft({
      items: rec.data.value.items.map((x) => ({ ...x, icon: normalizeIconKey(x.icon) })),
    });
  }, [rec.data, draft]);

  /** 哪一行正在挑图标（null = 都没开）。 */
  const [picking, setPicking] = useState<number | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/admin/settings/spaces`, {
        value: draft,
        expectedUpdatedAt: rec.data?.updatedAt ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      /** 🔴 前台那份也要作废，见文件头。 */
      void qc.invalidateQueries({ queryKey: ["site-config"] });
    },
  });

  if (rec.isPending) return <Spinner compact />;
  if (rec.error) return <ErrorBlock error={rec.error} />;
  if (!draft) return <Spinner compact />;

  /** 库里已经存过的那几个 key —— 它们身上挂着商品，不给改。 */
  const lockedKeys = new Set((rec.data?.value.items ?? []).map((x) => x.key));

  const patch = (i: number, next: Partial<SpaceRow>) =>
    setDraft({
      ...draft,
      items: draft.items.map((x, at) => (at === i ? { ...x, ...next } : x)),
    });

  const move = (i: number, delta: number) => {
    const to = i + delta;
    if (to < 0 || to >= draft.items.length) return;
    const items = [...draft.items];
    const [row] = items.splice(i, 1);
    items.splice(to, 0, row!);
    setDraft({ ...draft, items });
  };

  const remove = (i: number) =>
    setDraft({ ...draft, items: draft.items.filter((_, at) => at !== i) });

  /**
   * 整排换画法。
   *
   * ⚠️ 每个矢量图标都配了一个对应的 emoji（在 `space-icons.data.ts` 里），
   *    所以来回切是**认得回去的** —— 不会切过去再切回来就变成另一个图标。
   *    自己手打的 emoji 切回矢量图时按最接近的那个来（`emojiOf` 的反向查不到就退回全屋）。
   */
  const switchAll = (to: "emoji" | "vector") =>
    setDraft({
      ...draft,
      items: draft.items.map((x) => ({
        ...x,
        icon:
          to === "emoji"
            ? `${EMOJI_PREFIX}${emojiOf(x.icon)}`
            : (VECTOR_BY_EMOJI.get(emojiOf(x.icon)) ?? normalizeIconKey(x.icon)),
      })),
    });

  /**
   * 存之前自己拦一道。
   * ⚠️ 服务端也会拦（zod），但错误信息是给程序员看的；
   *    在这儿说清楚哪一行、错在哪，他才知道去改什么。
   */
  const problems: string[] = [];
  draft.items.forEach((row, i) => {
    const at = `第 ${i + 1} 格`;
    if (!row.key.trim()) problems.push(`${at}还没填标识`);
    else if (!/^[a-z][a-z0-9_]{0,23}$/.test(row.key))
      problems.push(`${at}的标识「${row.key}」不合法：只能小写字母开头，后面接小写字母、数字或下划线`);
    if (!row.label.trim()) problems.push(`${at}还没填名字`);
    /** ⚠️ 只有 `emoji:` 前缀、后面是空的话，首页那一格画出来是个空格。 */
    if (isEmojiIcon(row.icon) && !row.icon.slice(EMOJI_PREFIX.length).trim())
      problems.push(`${at}选了 emoji 但还没填是哪一个`);
  });
  const dupes = draft.items
    .map((x) => x.key)
    .filter((k, i, all) => k && all.indexOf(k) !== i);
  if (dupes.length) problems.push(`标识重复了：${[...new Set(dupes)].join("、")}`);
  if (draft.items.length === 0) problems.push("至少留一格");

  return (
    <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="space-y-6">
        <section className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[15px] font-medium">首页的格子（{draft.items.length}）</p>
            <div className="flex flex-wrap items-center gap-2">
              {/*
                🔴 **整排一键切。** 一格一格挑当然也行，但「换成 emoji 版」是
                   一个整体决定 —— 五格挨个点五遍还容易漏一格，出来就是
                   四个矢量图配一个 emoji，比两种都难看。
                ⚠️ 两个按钮都只改图标，名字 / 副标题 / 顺序一个不动。
              */}
              <Button size="sm" variant="outline" onClick={() => switchAll("emoji")}>
                整排换成 emoji
              </Button>
              <Button size="sm" variant="outline" onClick={() => switchAll("vector")}>
                整排换成矢量图
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDraft({ ...draft, items: [...draft.items, { ...BLANK }] })}
              >
                <Plus className="size-4" />
                加一格
              </Button>
            </div>
          </div>

          {/*
            ⚠️ 把 emoji 的两条代价写在这儿 —— 他要在**按下去之前**知道，
               而不是切完上线了才有人问"为什么我手机上那个沙发跟你电脑上不一样"。
          */}
          <p className="mb-4 max-w-[68ch] text-[12px] leading-relaxed text-muted-foreground">
            emoji 活泼、一眼认得出，但两件事要知道：它的样子由
            <strong className="font-medium text-foreground">每台设备自己的系统字体</strong>
            决定（同一个 🛋️ 在苹果、安卓、Windows 上是三种画风），而且
            <strong className="font-medium text-foreground">自带颜色</strong>
            、换网站配色时它不跟着走。矢量图这两条都没有问题。
          </p>

          <div className="space-y-3">
            {draft.items.map((row, i) => {
              const locked = lockedKeys.has(row.key);
              return (
                <div key={i} className="rounded-lg border border-border p-3">
                  <div className="flex flex-wrap items-start gap-3">
                    {/* 图标：点一下展开挑选 */}
                    <button
                      type="button"
                      onClick={() => setPicking(picking === i ? null : i)}
                      className={cn(
                        "flex size-12 shrink-0 items-center justify-center rounded-lg border transition-colors",
                        picking === i
                          ? "border-brand-500 bg-brand-50 text-brand-700"
                          : "border-border text-brand-600 hover:border-brand-300",
                      )}
                      title="换图标"
                    >
                      <SpaceIcon icon={row.icon} size={24} />
                    </button>

                    <div className="grid min-w-[240px] flex-1 gap-2 sm:grid-cols-2">
                      <label className="block">
                        <span className="mb-1 block text-[12px] text-muted-foreground">名字</span>
                        <Input
                          value={row.label}
                          onChange={(e) => patch(i, { label: e.target.value })}
                          placeholder="客厅"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-[12px] text-muted-foreground">
                          一行小字（只在网页版显示）
                        </span>
                        <Input
                          value={row.hint}
                          onChange={(e) => patch(i, { hint: e.target.value })}
                          placeholder="地板 · 灯具 · 墙面"
                        />
                      </label>
                      <label className="block sm:col-span-2">
                        <span className="mb-1 block text-[12px] text-muted-foreground">
                          标识
                          {locked ? (
                            /**
                             * 🔴 已经在用的 key 不给改 —— 商品和案例上打的标签比的就是它。
                             *    改了不会报错，只会让那一格永远是空的。
                             */
                            <span className="ml-2 text-amber-700">
                              已经有商品按它打标签了，不能再改
                            </span>
                          ) : (
                            <span className="ml-2">小写字母，会出现在地址栏上（?space=…）</span>
                          )}
                        </span>
                        <Input
                          value={row.key}
                          disabled={locked}
                          onChange={(e) => patch(i, { key: e.target.value.trim() })}
                          placeholder="living"
                        />
                      </label>
                    </div>

                    <div className="flex shrink-0 flex-col gap-1.5">
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => move(i, -1)}>
                          <ArrowUp className="size-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => move(i, 1)}>
                          <ArrowDown className="size-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(i)}>
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                      <label className="flex cursor-pointer items-center gap-1.5 text-[13px]">
                        <input
                          type="checkbox"
                          checked={row.enabled}
                          onChange={(e) => patch(i, { enabled: e.target.checked })}
                        />
                        首页显示
                      </label>
                    </div>
                  </div>

                  {/*
                    ⚠️ 删这一格**不会动商品上已经打的标签** —— 那些商品还在，
                       只是首页少了一个入口。所以删是安全的，停用更安全（随时能恢复）。
                  */}
                  {locked && !row.enabled && (
                    <p className="mt-2 text-[12px] text-muted-foreground">
                      已停用：首页不出这一格，商品上的标签不动，随时能打开。
                    </p>
                  )}

                  {picking === i && (
                    <IconPicker value={row.icon} onPick={(icon) => patch(i, { icon })} />
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {problems.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
            <p className="mb-1 font-medium">还差这些才能存：</p>
            <ul className="list-disc space-y-0.5 pl-5">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )}

        {save.error && <ErrorBlock error={save.error} />}
        <div className="flex items-center gap-3">
          <Button
            loading={save.isPending}
            disabled={problems.length > 0}
            onClick={() => save.mutate()}
          >
            保存
          </Button>
          {save.isSuccess && !save.isPending && (
            <span className="text-[13px] text-muted-foreground">已保存，去首页就能看到</span>
          )}
        </div>
      </div>

      {/* ── 右栏：预览。所见即所得，省得他保存完再跳去首页看一眼 ── */}
      <section className="overflow-hidden rounded-xl border border-border xl:sticky xl:top-6">
        <p className="border-b border-border bg-secondary/40 px-4 py-2 text-[13px] text-muted-foreground">
          预览
        </p>

        <div className="space-y-5 p-4">
          <div>
            <p className="mb-2 text-[12px] text-muted-foreground">网页版首页</p>
            <div className="rounded-xl bg-gradient-to-br from-brand-600 to-brand-500 p-4">
              <div
                className="grid gap-2"
                style={{
                  gridTemplateColumns: `repeat(${Math.max(
                    draft.items.filter((x) => x.enabled).length,
                    1,
                  )}, minmax(0, 1fr))`,
                }}
              >
                {draft.items
                  .filter((x) => x.enabled)
                  .map((s, i) => (
                    <div key={i} className="rounded-lg bg-white/15 p-2 text-center">
                      <SpaceIcon icon={s.icon} size={20} className="mx-auto text-white" />
                      <span className="mt-1 block text-[12px] font-medium text-white">
                        {s.label || "未命名"}
                      </span>
                      {/* ⚠️ 预览栏只有四百多宽，不截的话一行小字会折成三行，把版式撞散。 */}
                      <span className="mt-0.5 block truncate text-[10px] leading-tight text-white/70">
                        {s.hint}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[12px] text-muted-foreground">
              手机版首页（不显示那一行小字 —— 一行放五个，塞不下）
            </p>
            <div className="flex justify-around rounded-xl border border-border p-3">
              {draft.items
                .filter((x) => x.enabled)
                .map((s, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5">
                    <span className="flex size-10 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                      <SpaceIcon icon={s.icon} size={20} />
                    </span>
                    <span className="text-[11px] text-muted-foreground">{s.label || "未命名"}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────── 图标选择器 ───────────────────────

/**
 * 挑图标。
 *
 * 🔴 **用网格挑，不用下拉框** —— 图标是看着挑的，不是读名字挑的。
 *    （跟配色那一页同一个道理。）
 * ⚠️ 分「空间」「建材」两组：36 个平铺成一片时找一个特定的要扫一遍，
 *    分了组就只用看半片。
 * ⚠️ 带一个搜索框：知道自己要什么的人（"灯"）直接打字比用眼睛扫快。
 */
function IconPicker({ value, onPick }: { value: string; onPick: (icon: string) => void }) {
  const [q, setQ] = useState("");
  /** 当前这一格是哪种画法 —— 决定下面网格里画的是矢量图还是 emoji。 */
  const [mode, setMode] = useState<"vector" | "emoji">(isEmojiIcon(value) ? "emoji" : "vector");
  const kw = q.trim();

  const groups = ICON_GROUPS.map((g) => ({
    ...g,
    icons: kw ? g.icons.filter((i) => i.label.includes(kw) || i.key.includes(kw)) : g.icons,
  })).filter((g) => g.icons.length > 0);

  return (
    <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-3">
      {/*
        ⚠️ 两种画法用同一份清单（同一个图标的两种画法），所以切换只换渲染方式，
           挑中的还是同一格 —— 不会切一下就得重新找。
      */}
      <div className="mb-3 flex items-center gap-2">
        {(
          [
            ["vector", "矢量图"],
            ["emoji", "emoji"],
          ] as const
        ).map(([m, t]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-[13px] transition-colors",
              mode === m
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-border bg-background text-muted-foreground hover:border-brand-300",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mb-3 flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜图标：灯 / 瓷砖 / 防水…" />
      </div>

      {groups.length === 0 ? (
        <p className="py-4 text-center text-[13px] text-muted-foreground">
          没有叫「{kw}」的图标，换个词试试
        </p>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.group}>
              <p className="mb-1.5 text-[12px] text-muted-foreground">{g.group}</p>
              <div className="grid grid-cols-8 gap-1.5 sm:grid-cols-12">
                {g.icons.map((b) => {
                  const pick = mode === "emoji" ? `${EMOJI_PREFIX}${b.emoji}` : b.key;
                  const on =
                    mode === "emoji" ? value === pick : normalizeIconKey(value) === b.key;
                  return (
                    <button
                      key={b.key}
                      type="button"
                      title={b.label}
                      onClick={() => onPick(pick)}
                      className={cn(
                        "flex aspect-square items-center justify-center rounded-md border transition-colors",
                        on
                          ? "border-brand-500 bg-brand-50 text-brand-700"
                          : "border-border bg-background text-muted-foreground hover:border-brand-300 hover:text-foreground",
                      )}
                    >
                      <SpaceIcon icon={pick} size={20} />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === "emoji" && (
        /*
          ⚠️ 也允许自己打一个 —— 上面那 36 个是常用的，但"我就想放个 🐶"是合理需求，
             拦着他反而要来问。手打的在「整排换成矢量图」时查不到对应关系，会保持原样。
        */
        <div className="mt-3">
          <p className="mb-1.5 text-[12px] text-muted-foreground">或者自己打一个 emoji</p>
          <div className="flex items-center gap-2">
            <Input
              value={isEmojiIcon(value) ? value.slice(EMOJI_PREFIX.length) : ""}
              onChange={(e) => onPick(`${EMOJI_PREFIX}${e.target.value.trim()}`)}
              placeholder="🛋️"
            />
            <span className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-background">
              {isEmojiIcon(value) && value.length > EMOJI_PREFIX.length ? (
                <SpaceIcon icon={value} size={22} />
              ) : null}
            </span>
          </div>
        </div>
      )}

      {/* ⚠️ 图标是随站发布的，不依赖任何外部服务 —— 把这句写出来，省得有人去找"图标库设置"。 */}
      <p className="mt-3 text-[12px] text-muted-foreground">
        共 {ICON_GROUPS.reduce((n, g) => n + g.icons.length, 0)} 个，每个都有矢量图和 emoji
        两种画法，随网站一起发布，不依赖外部图标库。
      </p>
    </div>
  );
}
